import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  AssetCountPerRoom,
  AssetCreateInterface,
  AssetListFiltersOptions,
  AssetListFiltersWithPage,
  AssetPaginated,
  AssetPaginationEntry,
  DeleteAssetRequest
} from '@simOn/asset/element/models';
import { DuplicateAsset, TAsset, TCreateAsset, TUpdateAsset } from '@simOn/asset/modifying/models';
import { IPagination } from '@simOn/common/http';
import { TScanProvider } from '@simOn/common/scan';
import { BuildHttpParamsFromFilters } from '@simOn/utils/helpers';
import { API_URL } from '@simOn/utils/tokens';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AssetsApiService {
  private readonly http = inject(HttpClient);
  private readonly _apiUrl = inject(API_URL);

  getAsset(assetGuid: string): Observable<TAsset> {
    return this.http.get<TAsset>(
      `${this._apiUrl}/Assets/GetAsset?assetGuid=${assetGuid}&UserDateTime=${new Date().toJSON()}`
    );
  }

  getAssets(): Observable<TAsset[]> {
    return this.http.get<TAsset[]>(`${this._apiUrl}/Assets/GetAssets`);
  }

  getUsedScanTags(scanModelId: string, scanProviderType: TScanProvider): Observable<string[]> {
    let params = new HttpParams();
    params = params.append('id', scanModelId);
    params = params.append('scanProviderType', scanProviderType);
    return this.http.get<string[]>(`${this._apiUrl}/Assets/GetUsedScanTags?${params.toString()}`);
  }

  getNotAssignedAssets(apartmentGuid: string): Observable<TAsset[]> {
    return this.http.get<TAsset[]>(`${this._apiUrl}/Assets/GetNotAssignedAssets?apartmentGuid=${apartmentGuid}`);
  }

  createAsset(assetCreateData: TCreateAsset): Observable<TAsset> {
    return this.http.post<TAsset>(`${this._apiUrl}/Assets/CreateAsset`, assetCreateData);
  }
  createAssets(body: AssetCreateInterface[]): Observable<void> {
    return this.http.post<void>(`${this._apiUrl}/Assets/CreateAssets`, body);
  }

  updateAsset(assetCreateData: TUpdateAsset): Observable<TAsset> {
    return this.http.put<TAsset>(`${this._apiUrl}/Assets/UpdateAsset`, assetCreateData);
  }

  deleteAsset({ id }: DeleteAssetRequest): Observable<string> {
    return this.http.delete<string>(`${this._apiUrl}/Assets/DeleteAsset?assetGuid=${id}`);
  }

  orderAssetsForRoom(roomGuid: string, ...assets: string[]): Observable<void> {
    return this.http.post<void>(`${this._apiUrl}/Assets/OrderAssetsForRoom`, { roomGuid, assets });
  }

  getAssetCountPerRoom() {
    return this.http.get<AssetCountPerRoom[]>(`${this._apiUrl}/Assets/GetAssetCountPerRoom`);
  }

  getAssetsPaginated(filters: Partial<AssetListFiltersWithPage>): Observable<AssetPaginated> {
    const body = {
      pageFilter: {
        pageNumber: filters.pageNumber,
        pageSize: filters.pageSize
      },
      assetFilter: {
        categoryIds: filters.categoryIds,
        stagesImportedNotesIds: filters.stagesImportedNotesIds,
        editedAtFrom: filters.editedAtFrom,
        editedAtTo: filters.editedAtTo,
        authorIds: filters.authorIds,
        roomIds: filters.roomIds,
        name: filters.name,
        apartmentIds: filters.apartmentIds
      },
      assetSort: {
        sortBy: filters.sortBy,
        isAscending: filters.isAscending
      }
    };
    return this.http
      .post<IPagination<AssetPaginationEntry>>(`${this._apiUrl}/Assets/GetAssetsPaginated`, body)
      .pipe(
        map((response) => ({
          ...response,
          items: response.items.map((entry) => ({ ...entry.item, privileges: entry.privileges }))
        }))
      );
  }

  getAssetIndex(id: string, filters: Partial<AssetListFiltersWithPage>): Observable<number> {
    let params = BuildHttpParamsFromFilters<AssetListFiltersWithPage>(filters).set('assetId', id);
    return this.http.get<number>(`${this._apiUrl}/Assets/GetAssetIndex`, { params });
  }

  getFilteringOptions(filters: Partial<AssetListFiltersWithPage>): Observable<AssetListFiltersOptions> {
    const body = {
      categoryIds: filters.categoryIds,
      stagesImportedNotesIds: filters.stagesImportedNotesIds,
      editedAtFrom: filters.editedAtFrom,
      editedAtTo: filters.editedAtTo,
      authorIds: filters.authorIds,
      roomIds: filters.roomIds,
      name: filters.name,
      apartmentIds: filters.apartmentIds
    };
    return this.http.post<AssetListFiltersOptions>(`${this._apiUrl}/Assets/GetFilteringOptions`, body).pipe(
      // Every collection in the response DTO is nullable — normalize so consumers can map safely.
      map((options) => ({
        ...options,
        categories: options.categories ?? [],
        authors: options.authors ?? [],
        rooms: options.rooms ?? [],
        apartments: options.apartments ?? []
      }))
    );
  }

  duplicateAsset(data: DuplicateAsset): Observable<TAsset> {
    return this.http.post<TAsset>(`${this._apiUrl}/Assets/DuplicateAsset`, data);
  }

  getRoomAssetsCount(roomId: string): Observable<number> {
    return this.http.get<number>(`${this._apiUrl}/Assets/GetRoomAssetsCount`, {
      params: { RoomId: roomId }
    });
  }
}
